import dynamic from 'next/dynamic';
import { MouseEvent, useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import TrailPageType from '@alltrails/amplitude/enums/TrailPageType';
import logTrailDetailsActionTapped from '@alltrails/amplitude/events/logTrailDetailsActionTapped';
import logTrailDetailsMapViewed from '@alltrails/amplitude/events/logTrailDetailsMapViewed';
import usePublicBaseUrl from '@alltrails/context/hooks/usePublicBaseUrl';
import useUser from '@alltrails/context/hooks/useUser';
import Image from '@alltrails/core/components/Image';
import getStaticMapUrl from '@alltrails/core/utils/getStaticMapUrl';
import useIsTrailMapOpen from '@/hooks/useIsTrailMapOpen';
import useMapSignupBypass from '@/hooks/useMapSignupBypass';
import styles from './MobileStaticMap.module.scss';

const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });

const messages = defineMessages({
  EXPAND_MAP: { defaultMessage: 'Expand map' }
});

type MobileStaticMapProps = {
  mapId: number;
  trailName: string;
  trailId: number;
  trailPageType: TrailPageType;
};

const MobileStaticMap = ({ mapId, trailName, trailId, trailPageType }: MobileStaticMapProps) => {
  const intl = useIntl();
  const user = useUser();
  const { openTrailMap } = useIsTrailMapOpen();
  const { shouldBypassSignup, sendExposure } = useMapSignupBypass();
  const [isSignupOpen, setIsSignupOpen] = useState(false);
  const publicBaseUrl = usePublicBaseUrl();
  // Names the button and its tooltip so speech input users can say what they see.
  const expandLabel = intl.formatMessage(messages.EXPAND_MAP);

  const staticMapUrl = getStaticMapUrl({ baseUrl: publicBaseUrl, id: mapId, scale: 2, size: '160x160', type: 'maps' });

  useEffect(() => {
    logTrailDetailsMapViewed({ trail_id: trailId });
  }, [trailId]);

  const logStaticMapClicked = () => {
    logTrailDetailsActionTapped({
      trail_id: trailId.toString(),
      trail_page_action: 'view map',
      trail_page_location: 'hero image inset',
      trail_page_type: trailPageType
    });
  };

  const staticImage = (
    <Image
      alt={intl.formatMessage({ defaultMessage: '{name} map' }, { name: trailName })}
      className={styles.staticMap}
      fill
      loading="eager"
      sizes="70px"
      src={staticMapUrl}
    />
  );

  const onTrailMapClick = (e: MouseEvent) => {
    e.stopPropagation();
    logStaticMapClicked();
    if (!user) {
      sendExposure();
    }
    if (user || shouldBypassSignup) {
      openTrailMap();
    } else {
      setIsSignupOpen(true);
    }
  };

  return (
    <div className={styles.staticMapContainer}>
      <button aria-label={expandLabel} className={styles.button} onClick={onTrailMapClick} title={expandLabel}>
        {staticImage}
      </button>
      {isSignupOpen && (
        <SignUpModal
          isOpen={isSignupOpen}
          closeModal={() => setIsSignupOpen(false)}
          deeplinkData={{ pageId: trailId, pageType: 'trail' }}
          onSuccess={() => setIsSignupOpen(false)}
          trigger="View Static Map"
          upsellTrigger="View Static Map"
        />
      )}
    </div>
  );
};

export default MobileStaticMap;
